import apiClient from './api'

function asString(value, fallback = '') {
  if (value == null) return fallback
  return String(value)
}

function asNumber(value, fallback = null) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizeRelease(row = {}) {
  const longDescription = asString(row.longDescription || row.releaseNotes)
  return {
    id: asString(row.id),
    platform: asString(row.platform, 'android'),
    channel: asString(row.channel, 'debug'),
    packageName: asString(row.packageName),
    versionName: asString(row.versionName),
    versionCode: asNumber(row.versionCode, 0),
    minSupportedVersionCode: asNumber(row.minSupportedVersionCode, null),
    apkUrl: asString(row.apkUrl),
    sha256: asString(row.sha256).toLowerCase(),
    sizeBytes: asNumber(row.sizeBytes, null),
    shortDescription: asString(row.shortDescription),
    longDescription,
    releaseNotes: longDescription,
    mandatory: Boolean(row.mandatory),
    rolloutPercent: asNumber(row.rolloutPercent, 100),
    status: asString(row.status, 'draft'),
    publishedAt: row.publishedAt || null,
    expiresAt: row.expiresAt || null,
    createdAt: row.createdAt || null,
    updatedAt: row.updatedAt || null,
    qualificationEvidence: row.qualificationEvidence
      ? {
          status: asString(row.qualificationEvidence.status, 'unknown'),
          candidateId: asString(row.qualificationEvidence.candidateId),
          manifestSha256: asString(row.qualificationEvidence.manifestSha256).toLowerCase(),
          invalidatedAt: row.qualificationEvidence.invalidatedAt || null,
        }
      : null,
  }
}

function normalizeError(error, fallbackMessage) {
  return {
    status: Number(error?.response?.status || 0),
    message: error?.response?.data?.message || error?.message || fallbackMessage,
    code: error?.response?.data?.code || null,
  }
}

function normalizeListResponse(response) {
  const list = Array.isArray(response?.data?.data)
    ? response.data.data
    : Array.isArray(response?.data?.items)
      ? response.data.items
      : []

  const items = list.map(normalizeRelease)
  return {
    items,
    total: Number(response?.data?.total || items.length),
  }
}

function buildListParams(filters = {}) {
  const params = {}
  if (filters.platform) params.platform = filters.platform
  if (filters.channel) params.channel = filters.channel
  if (filters.status) params.status = filters.status

  const pageSize = asNumber(filters.pageSize, null)
  const limit = asNumber(filters.limit, null)
  if (pageSize != null && pageSize > 0) {
    params.limit = pageSize
  } else if (limit != null && limit > 0) {
    params.limit = limit
  }

  return params
}

export async function listMobileAppReleases(filters = {}) {
  try {
    const response = await apiClient.get('/admin/mobile/releases', {
      params: buildListParams(filters),
    })
    return normalizeListResponse(response)
  } catch (error) {
    throw normalizeError(error, 'Unable to list mobile app releases')
  }
}

export async function getMobileAppRelease(id) {
  const releaseId = asString(id).trim()
  if (!releaseId) {
    throw { status: 400, message: 'Release id is required' }
  }

  try {
    const response = await apiClient.get(`/admin/mobile/releases/${releaseId}`)
    return normalizeRelease(response?.data?.data || response?.data)
  } catch (error) {
    const status = Number(error?.response?.status || 0)
    if (status !== 404) {
      throw normalizeError(error, 'Unable to load mobile app release detail')
    }
  }

  // WHY: current backend exposes list endpoint but may not expose detail endpoint yet.
  try {
    const response = await listMobileAppReleases({ limit: 200 })
    const found = response.items.find((item) => item.id === releaseId)
    if (found) return found
    throw { status: 404, message: 'Release not found' }
  } catch (error) {
    throw normalizeError(error, 'Unable to load mobile app release detail')
  }
}

export async function createMobileAppRelease(payload) {
  try {
    const response = await apiClient.post('/admin/mobile/releases', payload)
    return normalizeRelease(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to create mobile app release')
  }
}

export async function updateMobileAppRelease(id, payload) {
  const releaseId = asString(id).trim()
  if (!releaseId) {
    throw { status: 400, message: 'Release id is required' }
  }

  try {
    const response = await apiClient.put(`/admin/mobile/releases/${releaseId}`, payload)
    return normalizeRelease(response?.data?.data || response?.data)
  } catch (error) {
    const status = Number(error?.response?.status || 0)
    if (status === 404) {
      throw {
        status: 404,
        message: 'Backend endpoint required: /api/admin/mobile/releases/:id',
      }
    }
    throw normalizeError(error, 'Unable to update mobile app release')
  }
}

export async function uploadMobileAppReleaseApk(id, file, onProgress) {
  const releaseId = asString(id).trim()
  if (!releaseId) {
    throw { status: 400, message: 'Release id is required' }
  }

  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await apiClient.post(`/admin/mobile/releases/${releaseId}/apk`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => {
        if (!event?.total || typeof onProgress !== 'function') return
        const percent = Math.round((event.loaded / event.total) * 100)
        onProgress(percent)
      },
    })

    return {
      apkUrl: asString(response?.data?.data?.apkUrl || response?.data?.apkUrl),
      sha256: asString(response?.data?.data?.sha256 || response?.data?.sha256).toLowerCase(),
      sizeBytes: asNumber(response?.data?.data?.sizeBytes || response?.data?.sizeBytes, null),
    }
  } catch (error) {
    const status = Number(error?.response?.status || 0)
    if (status === 404 || status === 405) {
      throw {
        status,
        message: 'APK upload endpoint is not available. Use APK URL + SHA-256 metadata mode.',
      }
    }
    throw normalizeError(error, 'Unable to upload APK artifact')
  }
}

export async function publishMobileAppRelease(id) {
  const releaseId = asString(id).trim()
  if (!releaseId) {
    throw { status: 400, message: 'Release id is required' }
  }

  try {
    const response = await apiClient.post(`/admin/mobile/releases/${releaseId}/publish`)
    return normalizeRelease(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to publish mobile app release')
  }
}

export async function revokeMobileAppRelease(id, reason = '') {
  const releaseId = asString(id).trim()
  if (!releaseId) {
    throw { status: 400, message: 'Release id is required' }
  }

  try {
    const response = await apiClient.post(`/admin/mobile/releases/${releaseId}/revoke`, {
      reason: asString(reason),
    })
    return normalizeRelease(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to revoke mobile app release')
  }
}

export async function deleteMobileAppRelease(id) {
  const releaseId = asString(id).trim()
  if (!releaseId) {
    throw { status: 400, message: 'Release id is required' }
  }

  try {
    const response = await apiClient.delete(`/admin/mobile/releases/${releaseId}`)
    return normalizeRelease(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to delete mobile app release')
  }
}

export async function getMobileAppReleaseEvents(id) {
  const releaseId = asString(id).trim()
  if (!releaseId) {
    throw { status: 400, message: 'Release id is required' }
  }

  try {
    const response = await apiClient.get(`/admin/mobile/releases/${releaseId}/events`)
    const rows = Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data?.items)
        ? response.data.items
        : []

    return rows.map((row) => ({
      status: asString(row.status),
      userId: row.userId || null,
      deviceIdHash: row.deviceIdHash || null,
      packageName: asString(row.packageName),
      fromVersionCode: asNumber(row.fromVersionCode, null),
      toVersionCode: asNumber(row.toVersionCode, null),
      error: asString(row.error),
      createdAt: row.createdAt || null,
      updatedAt: row.updatedAt || null,
    }))
  } catch (error) {
    const status = Number(error?.response?.status || 0)
    if (status === 404) {
      throw {
        status: 404,
        message: 'Telemetry endpoint is not available yet.',
      }
    }
    throw normalizeError(error, 'Unable to load release telemetry')
  }
}

export async function previewMobileAppReleaseSelection(payload) {
  try {
    const response = await apiClient.post('/admin/mobile/releases/preview-selection', payload)
    return response?.data?.data || response?.data || {}
  } catch (error) {
    const status = Number(error?.response?.status || 0)
    if (status !== 404) {
      throw normalizeError(error, 'Unable to preview release selection')
    }
  }

  // WHY: fallback keeps preview useful when dedicated admin preview endpoint is missing.
  try {
    const response = await apiClient.get('/mobile/app-config', {
      params: {
        platform: asString(payload?.platform || 'android').toLowerCase(),
        channel: asString(payload?.channel || 'debug').toLowerCase(),
        packageName: asString(payload?.packageName),
        versionCode: asNumber(payload?.currentVersionCode, null),
        version: asString(payload?.currentVersionName || '0.0.0'),
        deviceId: asString(payload?.deviceIdHash || payload?.deviceId || ''),
      },
    })

    const update = response?.data?.update
    if (!update?.available) {
      return { selectedRelease: null, reason: 'no_matching_release' }
    }

    return {
      selectedRelease: {
        id: asString(update.releaseId),
        versionCode: asNumber(update.versionCode, null),
        versionName: asString(update.versionName),
        mandatory: Boolean(update.mandatory),
        rolloutPercent: null,
      },
      reason: 'matched_via_mobile_app_config',
      rawUpdate: update,
    }
  } catch (error) {
    throw normalizeError(error, 'Unable to preview release selection')
  }
}

const mobileAppReleaseService = {
  listMobileAppReleases,
  getMobileAppRelease,
  createMobileAppRelease,
  updateMobileAppRelease,
  uploadMobileAppReleaseApk,
  publishMobileAppRelease,
  revokeMobileAppRelease,
  getMobileAppReleaseEvents,
  previewMobileAppReleaseSelection,
}

export default mobileAppReleaseService
